/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-static-element-interactions */
import uploadIcon from 'assets/images/uploadIcon.png';
import axios from 'axios';
import { Button, Input, SimpleSelect } from 'components/atoms';
import { useGetCategoryList, useGetImageUploadURL, useUpdateVideo } from 'framework/api/methods';
import { FC, useCallback, useEffect, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

export interface VideoUpdateProps {
  [x: string]: any;
}

export const VideoUpdate: FC<VideoUpdateProps> = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { id } = useParams();
  const notify = (message: any, type: any) => {
    if (type === 'error') {
      toast.error(message, {
        position: 'bottom-center',
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
    }
    if (type === 'success') {
      toast.success(message, {
        position: 'bottom-center',
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
    }
  };

  const [imageAttachment, setImageAttachments] = useState<any>([]);
  const [isNotAImage, setIsNotAImage] = useState(false);
  const [uploadProgressImage, setUploadProgressImage] = useState(0);
  const [uploadedImageUrl, setUploadedImageUrl] = useState('');
  const [currentImage, setCurrentImage] = useState('');
  const [title, setTitle] = useState('');

  useEffect(() => {
    setTitle(location.state?.title ?? '');
    setCurrentImage(location?.state?.imageUrl ?? '');
  }, [location]);

  const { mutate: getImageUploadURLMutate } = useGetImageUploadURL();

  // function to image upload
  const getImageUploadURL = (acceptedFiles: any) => {
    setUploadProgressImage(1);
    getImageUploadURLMutate(
      {
        fileName: acceptedFiles.name,
        fileType: acceptedFiles.type,
      },
      {
        onSuccess: async ({ data }: any) => {
          setUploadProgressImage(1);
          try {
            await axios.put(data.uploadUrl, acceptedFiles, {
              headers: {
                'Content-Type': acceptedFiles.type,
              },
              onUploadProgress: (progressEvent: any) => {
                const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                setUploadProgressImage(percent);
              },
            });
            setUploadedImageUrl(data.uniqueFileName.replace(/^covers\//, ''));
            notify('Image uploaded completed!', 'success');
            setUploadProgressImage(0);
          } catch (error) {
            setUploadProgressImage(0);
            notify('Image upload failed!', 'error');
            console.error(error);
          }
        },
        onError: ({ response: { data: errData }, response }: any) => {
          setUploadProgressImage(0);
          if (response?.status === 405 || response?.status === 403 || response?.status === 501) {
            notify('Session expired.', 'error');
            setTimeout(() => {
              navigate('/auth/login');
              localStorage.clear();
            }, 2000);
          } else {
            notify(errData?.message || 'Something went wrong.', 'error');
          }
        },
      },
    );
  };

  /** handle on drop method for image */
  const onDropImage = useCallback(
    (acceptedFiles: File[], fileRejections: any[]) => {
      if (acceptedFiles.length > 0) {
        const file = acceptedFiles[0];
        const image = new Image();

        image.src = URL.createObjectURL(file);

        image.onload = () => {
          const ratio = image.width / image.height;
          const isSixteenByNine = Math.abs(ratio - 16 / 9) < 0.01; // Small tolerance

          if (isSixteenByNine) {
            setImageAttachments([file]);
            setIsNotAImage(false);
            getImageUploadURL(file);
          } else {
            setImageAttachments([]);
            setUploadedImageUrl('');
            setIsNotAImage(true);
            notify('Only 16:9 aspect ratio images are allowed.', 'error');
          }

          URL.revokeObjectURL(image.src); // Clean up
        };

        image.onerror = () => {
          setImageAttachments([]);
          setUploadedImageUrl('');
          setIsNotAImage(true);
          notify('Invalid image file.', 'error');
        };
      } else if (fileRejections.length > 0) {
        setImageAttachments([]);
        setIsNotAImage(true);
      }
    },
    [imageAttachment],
  );

  const { getRootProps: getRootPropsImg, getInputProps: getInputPropsImg } = useDropzone({
    onDrop: onDropImage,
    accept: {
      'image/jpeg': ['.jpg', '.jpeg'],
      'image/png': ['.png'],
    },
    multiple: false,
  });

  const { mutate: updateVideoMutate } = useUpdateVideo();

  const updateVideo = () => {
    updateVideoMutate(
      {
        vid: id?.toString() || '',
        title,
        coverImageUrl:
          uploadedImageUrl !== '' ? uploadedImageUrl : currentImage.split('/').pop() ?? '',
      },
      {
        onSuccess: async () => {
          notify('Updated successfully!', 'success');
          setTimeout(() => {
            navigate('/videos');
          }, 1000);
        },
        onError: ({ response: { data: errData }, response }: any) => {
          if (response?.status === 405 || response?.status === 403 || response?.status === 501) {
            notify('Session expired.', 'error');
            setTimeout(() => {
              navigate('/auth/login');
              localStorage.clear();
            }, 2000);
          } else {
            notify(errData?.message || 'Something went wrong.', 'error');
          }
        },
      },
    );
  };

  return (
    <div className="flex flex-col justify-center items-center w-full h-full p-8 overflow-y-auto">
      <div className="bg-[white] shadow-md px-10 py-4 flex flex-col justify-center items-center w-2/3 h-[85%] rounded">
        <div className="flex w-full justify-center items-center h-fit text-[#F7941E] pb-5">
          Update your video details
        </div>

        <Input
          labelValue="Title"
          inputClassName="!rounded"
          appearance="gray"
          value={title}
          onChange={(e: any) => {
            setTitle(e.target.value);
          }}
        />

        <div className="flex mt-5 w-full gap-x-4">
          {!currentImage ? (
            <div className="w-1/2">
              <span className="text-[#737791] pb-1 text-sm">Thumbnail</span>

              <div>
                {imageAttachment?.length === 0 && (
                  <div
                    {...getRootPropsImg()}
                    className="flex flex-col p-[22px] border-2 border-dashed border-[#D9D9D5] rounded justify-center items-center cursor-pointer"
                  >
                    <input {...getInputPropsImg()} />
                    <img alt="UploadIcon" src={uploadIcon} width={32} height={32} />

                    <span className="text-[#737791] font-semibold text-sm">
                      Upload 360 video file
                    </span>

                    <span className="text-xs font-normal text-[#737791]">
                      Drag and drop file here
                    </span>

                    <span className="text-[8px] font-normal text-[#737791] text-center">
                      ( Ratio 16:9 - 1280×720, 1920×1080, 2560×1440 )
                    </span>
                  </div>
                )}

                {imageAttachment.map((file: any) => (
                  <li
                    key={file.path}
                    className="flex justify-between items-center bg-neutral-300 rounded w-full px-2 py-1 gap-x-4 mt-1"
                  >
                    <div className="w-full flex flex-col justify-start items-start gap-x-2 ">
                      <span className="flex w-full break-all line-clamp-1 text-sm text-neutral-700 font-normal">
                        {file.name}
                      </span>
                    </div>
                    <div className="w-fit">
                      {uploadProgressImage === 0 && (
                        <i
                          className="ri-close-line"
                          onClick={() => {
                            setImageAttachments([]);
                          }}
                        />
                      )}
                    </div>
                  </li>
                ))}
                {uploadProgressImage > 0 && (
                  <div className="w-full bg-gray-200 rounded h-2 mt-2">
                    <div
                      className="bg-[#F7941E] h-2 rounded"
                      style={{ width: `${uploadProgressImage}%` }}
                    />
                  </div>
                )}
              </div>
              {isNotAImage && (
                <div className="text-xs text-red-600 text-center w-full">Unsupported file.</div>
              )}
            </div>
          ) : (
            <div className="w-1/2">
              <span className="text-[#737791] pb-1 text-sm">Thumbnail</span>
              <div className="relative w-fit">
                <img
                  src={currentImage}
                  alt="img-video"
                  className="h-[150px] w-[280px] object-cover rounded"
                />
                <i
                  className="ri-close-line absolute top-0 right-1 text-white p-1 w-[20px] h-[20px] text-[16px] leading-none justify-center items-center flex rounded-full bg-red-600 border border-white"
                  onClick={() => {
                    setCurrentImage('');
                  }}
                />
              </div>
            </div>
          )}
        </div>
        <div className="mt-8 flex w-full justify-start items-start">
          <Button
            isRounded
            disabled={uploadProgressImage > 0 || (!uploadedImageUrl && !currentImage) || !title}
            onClick={() => {
              updateVideo();
            }}
          >
            Update
          </Button>

          <Button
            isRounded
            appearance="secondary"
            className="ml-2 border-2"
            onClick={() => {
              navigate('/videos');
            }}
          >
            Cancel
          </Button>
        </div>
      </div>
    </div>
  );
};

export default VideoUpdate;
